import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    TextField,
    InputAdornment,
    Grid,
    useTheme as useMuiTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Snackbar,
    Tooltip,
    TablePagination,
    CircularProgress,
    Divider,
    Button
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    CheckCircle as SuccessIcon
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import LogView from './LogView';
import { loggingApi } from '../../services/loggingApi';

interface Log {
    id: number;
    user_id: number;
    user?: {
        id: number;
        name: string;
        email: string;
    };
    module: string;
    activity: string;
    description: string;
    ip_address: string;
    user_agent: string;
    created_at: string;
    updated_at: string;
}

interface LogsState {
    logs: Log[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    } | null;
    filters: {
        modules: string[];
        activities: string[];
    };
    loading: boolean;
    error: string | null;
}

const Logs = () => {
    const muiTheme = useMuiTheme();
    const { theme: appTheme } = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    // Local state
    const [logsState, setLogsState] = useState<LogsState>({
        logs: [],
        pagination: null,
        filters: {
            modules: [],
            activities: []
        },
        loading: false,
        error: null
    });
    const [openView, setOpenView] = useState(false);
    const [selectedLog, setSelectedLog] = useState<Log | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [moduleFilter, setModuleFilter] = useState('');
    const [activityFilter, setActivityFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    // Load logs on component mount
    useEffect(() => {
        loadLogs();
    }, [page, rowsPerPage]);

    const loadLogs = async () => {
        setLogsState(prev => ({ ...prev, loading: true, error: null }));
        try {
            const params = {
                page: page + 1,
                per_page: rowsPerPage,
                query: searchQuery,
                module: moduleFilter,
                activity: activityFilter
            };
            const response = await loggingApi.getLogs(params);
            setLogsState(prev => ({
                ...prev,
                logs: response.logs,
                pagination: response.pagination,
                filters: response.filters,
                loading: false
            }));
        } catch (err) {
            setLogsState(prev => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Failed to load logs',
                loading: false
            }));
        }
    };

    // Handle search
    const handleSearch = () => {
        setPage(0);
        loadLogs();
    };

    // Handle module filter
    const handleModuleFilter = (module: string) => {
        setModuleFilter(module);
        setPage(0);
    };

    // Handle activity filter
    const handleActivityFilter = (activity: string) => {
        setActivityFilter(activity);
        setPage(0);
    };

    // Clear filters
    const handleClearFilters = () => {
        setSearchQuery('');
        setModuleFilter('');
        setActivityFilter('');
        setPage(0);
        loadLogs();
    };

    // Handle pagination
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle log actions
    const handleViewLog = (log: Log) => {
        setSelectedLog(log);
        setOpenView(true);
    };

    const handleViewClose = () => {
        setOpenView(false);
        setSelectedLog(null);
    };

    const getActivityIcon = (activity: string) => {
        const activityLower = activity.toLowerCase();
        if (activityLower.includes('create') || activityLower.includes('add')) {
            return <SuccessIcon color="success" />;
        } else if (activityLower.includes('update') || activityLower.includes('edit')) {
            return <InfoIcon color="info" />;
        } else if (activityLower.includes('delete') || activityLower.includes('remove')) {
            return <ErrorIcon color="error" />;
        } else if (activityLower.includes('login') || activityLower.includes('logout')) {
            return <WarningIcon color="warning" />;
        }
        return <InfoIcon color="info" />;
    };

    if (logsState.loading && logsState.logs.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: muiTheme.palette.text.primary }}>
                    System Logs
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Monitor system activities and user actions
                </Typography>
            </Box>

            {/* Filters */}
            <Card sx={{ mb: 3, bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search logs..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Module</InputLabel>
                                <Select
                                    value={moduleFilter}
                                    onChange={(e) => handleModuleFilter(e.target.value)}
                                    label="Module"
                                >
                                    <MenuItem value="">All Modules</MenuItem>
                                    {logsState.filters.modules.map((module) => (
                                        <MenuItem key={module} value={module}>
                                            {module}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Activity</InputLabel>
                                <Select
                                    value={activityFilter}
                                    onChange={(e) => handleActivityFilter(e.target.value)}
                                    label="Activity"
                                >
                                    <MenuItem value="">All Activities</MenuItem>
                                    {logsState.filters.activities.map((activity) => (
                                        <MenuItem key={activity} value={activity}>
                                            {activity}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<FilterIcon />}
                                onClick={handleSearch}
                                fullWidth
                            >
                                Search
                            </Button>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                fullWidth
                            >
                                Clear
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Logs Table */}
            <Card sx={{ bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: muiTheme.palette.action.hover }}>
                                <TableCell sx={{ fontWeight: 600 }}>Activity</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logsState.logs.map((log) => (
                                <TableRow key={log.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getActivityIcon(log.activity)}
                                            <Typography variant="body2">
                                                {log.activity}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.module}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {log.user?.name || 'System'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {log.user?.email || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ 
                                            maxWidth: 200,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {log.description}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {new Date(log.created_at).toLocaleDateString()}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {new Date(log.created_at).toLocaleTimeString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewLog(log)}
                                                sx={{ color: muiTheme.palette.info.main }}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {logsState.pagination && (
                    <TablePagination
                        component="div"
                        count={logsState.pagination.total}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                    />
                )}
            </Card>

            {/* Error Snackbar */}
            <Snackbar
                open={!!logsState.error}
                autoHideDuration={6000}
                onClose={() => setLogsState(prev => ({ ...prev, error: null }))}
            >
                <Alert severity="error" onClose={() => setLogsState(prev => ({ ...prev, error: null }))}>
                    {logsState.error}
                </Alert>
            </Snackbar>

            {/* Log View Dialog */}
            {openView && selectedLog && (
                <LogView
                    open={openView}
                    log={selectedLog}
                    onClose={handleViewClose}
                />
            )}
        </Box>
    );
};

export default Logs;
